import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { agentRepositoryToken } from './repository';
import type { AgentDetail } from './types';

interface AgentFormValues {
  readonly partnerId: string;
  readonly code: string;
  readonly name: string;
  readonly contact: string;
  readonly reason: string;
}

export function AgentFormDialog({
  agent,
  onClose,
  onSaved,
  open,
}: {
  readonly agent?: AgentDetail | undefined;
  readonly onClose: () => void;
  readonly onSaved: (agent: AgentDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(agentRepositoryToken);
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<AgentFormValues>({ defaultValues: defaults(agent) });

  useEffect(() => {
    if (!open) return;
    reset(defaults(agent));
    setError(null);
    submittingRef.current = false;
    setSubmitting(false);
  }, [agent, open, reset]);

  const submit = handleSubmit(async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const contact = parseContact(values.contact);
      const response =
        agent === undefined
          ? await repository.create({
              partnerId: values.partnerId.trim(),
              code: values.code.trim(),
              name: values.name.trim(),
              contact,
              reason: values.reason.trim(),
            })
          : await repository.update(agent.id, {
              name: values.name.trim(),
              contact,
              reason: values.reason.trim(),
            });
      onSaved(response.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('agents.errors.mutation')));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  });

  const close = () => {
    if (!submittingRef.current) onClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={close} open={open}>
      <DialogTitle>
        {t(agent === undefined ? 'agents.form.createTitle' : 'agents.form.editTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="agent-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            disabled={submitting || agent !== undefined}
            error={errors.partnerId !== undefined}
            helperText={errors.partnerId?.message}
            label={t('agents.fields.partnerId')}
            required
            {...register('partnerId', {
              required: t('agents.validation.partnerRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('agents.validation.partnerRequired'),
            })}
          />
          <TextField
            disabled={submitting || agent !== undefined}
            error={errors.code !== undefined}
            helperText={errors.code?.message}
            label={t('agents.fields.code')}
            required
            {...register('code', {
              required: t('agents.validation.codeRequired'),
              pattern: {
                value: /^[A-Za-z0-9][A-Za-z0-9_-]{1,31}$/u,
                message: t('agents.validation.codeInvalid'),
              },
            })}
          />
          <TextField
            autoFocus={agent !== undefined}
            disabled={submitting}
            error={errors.name !== undefined}
            helperText={errors.name?.message}
            label={t('agents.fields.name')}
            required
            {...register('name', {
              required: t('agents.validation.nameRequired'),
              validate: (value) => value.trim().length > 0 || t('agents.validation.nameRequired'),
            })}
          />
          <TextField
            disabled={submitting}
            error={errors.contact !== undefined}
            helperText={errors.contact?.message ?? t('agents.form.contactHelp')}
            label={t('agents.fields.contact')}
            minRows={3}
            multiline
            {...register('contact', {
              validate: (value) => isContact(value) || t('agents.validation.contactInvalid'),
            })}
          />
          <TextField
            disabled={submitting}
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('agents.fields.reason')}
            minRows={2}
            multiline
            required
            {...register('reason', {
              required: t('agents.validation.reasonRequired'),
              validate: (value) => value.trim().length > 0 || t('agents.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="agent-form" loading={submitting} type="submit" variant="contained">
          {t(agent === undefined ? 'actions.create' : 'actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function defaults(agent: AgentDetail | undefined): AgentFormValues {
  return {
    partnerId: agent?.partnerId ?? '',
    code: agent?.code ?? '',
    name: agent?.name ?? '',
    contact: JSON.stringify(agent?.contact ?? {}, null, 2),
    reason: '',
  };
}

function parseContact(value: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(value);
  if (!isPlainObject(parsed)) throw new TypeError('Contact must be a JSON object');
  return parsed;
}

function isContact(value: string): boolean {
  try {
    return isPlainObject(JSON.parse(value));
  } catch {
    return false;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
